import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import productIcon from "../../assets/svg/products__inactive_ic.svg";

const DeliveredModal = ({ show, onHide, onSubmit, products }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (products && products.length > 0) {
      const initialProducts = products.map((product) => ({
        ...product,
        returnQuantity: 0,
        dueQuantity: 0,
        maxReturn: parseInt(product.quantity) || 0,
        maxDue: parseInt(product.quantity) || 0,
      }));
      setProductList(initialProducts);
      calculateTotal(initialProducts);
    }
  }, [products]);

  const calculateTotal = (list) => {
    const total = list.reduce((sum, product) => {
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity) || 0;
      const returnQty = parseInt(product.returnQuantity) || 0;
      const dueQty = parseInt(product.dueQuantity) || 0;

      // Calculate delivered quantity (original - return - due)
      const deliveredQty = quantity - returnQty - dueQty;
      const singlePrice = parseFloat(product.singleprice) || (quantity > 0 ? price / quantity : 0);

      return sum + deliveredQty * singlePrice;
    }, 0);

    setTotalAmount(total);
  };

  const handleQuantityChange = (index, field, value) => {
    const newList = [...productList];
    const product = newList[index];
    const numValue = parseInt(value) || 0;

    if (field === "returnQuantity") {
      const maxAllowed = product.maxReturn - parseInt(product.dueQuantity || 0);
      product.returnQuantity = Math.max(0, Math.min(numValue, maxAllowed));
    } else if (field === "dueQuantity") {
      const maxAllowed = product.maxDue - parseInt(product.returnQuantity || 0);
      product.dueQuantity = Math.max(0, Math.min(numValue, maxAllowed));
    }

    setProductList(newList);
    calculateTotal(newList);
  };

  const incrementQuantity = (index, field) => {
    const product = productList[index];
    const currentValue = parseInt(product[field]) || 0;
    const maxValue = field === "returnQuantity" ? product.maxReturn - parseInt(product.dueQuantity || 0) : product.maxDue - parseInt(product.returnQuantity || 0);

    if (currentValue < maxValue) {
      handleQuantityChange(index, field, currentValue + 1);
    }
  };

  const decrementQuantity = (index, field) => {
    const product = productList[index];
    const currentValue = parseInt(product[field]) || 0;

    if (currentValue > 0) {
      handleQuantityChange(index, field, currentValue - 1);
    }
  };

  const handleSubmit = async () => {
    console.log("=== BUTTON CLICKED ===");
    console.log("DeliveredModal handleSubmit triggered");
    console.log("onSubmit prop exists:", typeof onSubmit === "function");
    console.log("productList:", productList);

    const payload = {
      products: productList.map((product) => ({
        id: product.id,
        ret_qty: parseInt(product.returnQuantity) || 0,
        due_qty: parseInt(product.dueQuantity) || 0,
      })),
    };

    console.log("DeliveredModal payload:", payload);

    if (typeof onSubmit !== "function") {
      console.error("ERROR: onSubmit is not a function!");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Calling onSubmit with payload...");
      await onSubmit(payload);
      console.log("onSubmit completed successfully");
    } catch (err) {
      console.error("Error submitting delivered data:", err);
    } finally {
      setIsLoading(false);
      console.log("=== BUTTON HANDLER COMPLETE ===");
    }
  };

  const handleClose = () => {
    setProductList([]);
    setTotalAmount(0);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="md" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="fw-700 fs-18">Delivered</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px", maxHeight: "500px", overflowY: "auto" }}>
        <div className="mb-3">
          <span className="fs-14 fw-500 text-muted">
            {productList.length} product{productList.length !== 1 ? "s" : ""}
          </span>
        </div>

        {productList.map((product, index) => (
          <div
            key={index}
            className="mb-3 p-3 rounded-3"
            style={{
              backgroundColor: "#F8F8F8",
              border: "1px solid #EBEBEB",
            }}
          >
            {/* Product Header */}
            <div className="d-flex align-items-start gap-3 mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2"
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FFE5CC",
                  flexShrink: 0,
                }}
              >
                <img
                  src={product.image || productIcon}
                  alt={product.item}
                  style={{
                    width: "32px",
                    height: "32px",
                    objectFit: "contain",
                  }}
                />
              </div>
              <div className="flex-grow-1">
                <div className="fs-14 fw-600 textFontColor">{product.item || "Product"}</div>
                <div className="fs-12 fw-400 text-muted mt-1">
                  Total quantity: <span className="fw-600">{product.quantity || 0}</span>
                </div>
              </div>
              <div className="fs-16 fw-600 textFontColor">₹{parseFloat(product.price || 0).toLocaleString()}</div>
            </div>

            {/* Return Quantity */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fs-13 fw-500 textFontColor">Return quantity</span>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => decrementQuantity(index, "returnQuantity")}
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: 0,
                  }}
                  disabled={!product.returnQuantity || product.returnQuantity === 0}
                >
                  <span style={{ fontSize: "18px", lineHeight: "1" }}>−</span>
                </button>
                <input
                  type="text"
                  className="form-control text-center"
                  value={product.returnQuantity || 0}
                  onChange={(e) => handleQuantityChange(index, "returnQuantity", e.target.value)}
                  style={{
                    width: "60px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                />
                <button
                  className="btn btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => incrementQuantity(index, "returnQuantity")}
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: 0,
                  }}
                  disabled={parseInt(product.returnQuantity || 0) + parseInt(product.dueQuantity || 0) >= product.maxReturn}
                >
                  <span style={{ fontSize: "18px", lineHeight: "1" }}>+</span>
                </button>
              </div>
            </div>

            {/* Due Quantity */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="fs-13 fw-500 textFontColor">Due quantity</span>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => decrementQuantity(index, "dueQuantity")}
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: 0,
                  }}
                  disabled={!product.dueQuantity || product.dueQuantity === 0}
                >
                  <span style={{ fontSize: "18px", lineHeight: "1" }}>−</span>
                </button>
                <input
                  type="text"
                  className="form-control text-center"
                  value={product.dueQuantity || 0}
                  onChange={(e) => handleQuantityChange(index, "dueQuantity", e.target.value)}
                  style={{
                    width: "60px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                />
                <button
                  className="btn btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => incrementQuantity(index, "dueQuantity")}
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: 0,
                  }}
                  disabled={parseInt(product.returnQuantity || 0) + parseInt(product.dueQuantity || 0) >= product.maxDue}
                >
                  <span style={{ fontSize: "18px", lineHeight: "1" }}>+</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center" style={{ borderTop: "1px solid #EBEBEB", padding: "16px 20px" }}>
        <div>
          <div className="fs-20 fw-700 textFontColor">₹{totalAmount.toLocaleString()}</div>
          <div className="fs-12 fw-400 text-muted">Total amount</div>
        </div>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className="rounded-pill px-4"
          disabled={isLoading}
          style={{
            backgroundColor: "#3469E7",
            borderColor: "#3469E7",
            height: "40px",
            minWidth: "120px",
          }}
        >
          {isLoading ? <Spinner animation="border" size="sm" /> : "Delivered"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeliveredModal;
